import React, { useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { ExpandableTextView } from '@modernloop/shared/components';
import { CaretRightIcon, InfoIcon, LockIcon } from '@modernloop/shared/icons';
import { Box, ListItem, ListItemButton, ListItemText, Stack, Tooltip, Typography } from '@mui/material';

interface Props {
  settingName: string | React.ReactNode;
  settingSubtitle?: string | React.ReactNode; // caption under the setting name
  settingValue: React.ReactNode;
  settingSubvalue?: React.ReactNode;
  isLocked?: boolean;
  onClick?: () => void;
  disabled: boolean;
  isOptional?: boolean;
  helpText?: string;
}

/**
 * Renders a clickable settings row with a left-side label and right-side value.
 */
export const SettingsRow: React.FC<Props> = ({
  settingName,
  settingSubtitle,
  settingValue,
  settingSubvalue,
  isLocked,
  onClick,
  disabled,
  isOptional,
  helpText,
}) => {
  const { t } = useTranslation();
  const buttonSxProps = useMemo(() => {
    const sx = {
      px: 1,
      display: 'flex',
      cursor: onClick ? 'pointer' : 'default',
    };
    if (!onClick) {
      return {
        ...sx,
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&.Mui-focusVisible': {
          backgroundColor: 'transparent',
        },
      };
    }
    return sx;
  }, [onClick]);

  return (
    <>
      <ListItem
        sx={{
          p: 0,
          width: 'calc(100% + 16px)',
          mx: -1,
          borderRadius: 1,
        }}
      >
        <ListItemButton sx={buttonSxProps} data-testid="SettingsRow-Button" onClick={onClick} disabled={disabled}>
          <ListItemText
            primary={
              <Stack direction="row" spacing={0.5} display="inline-flex" flexWrap="wrap">
                <Typography variant="body1">{settingName}</Typography>
                {helpText && (
                  <Tooltip title={helpText}>
                    {/* `disabled` on the parent ListItemButton sets pointer-events: none, which
                        would also block hover on this icon. Re-enable it so the tooltip stays
                        reachable when the row is disabled. */}
                    <Box display="flex" alignItems="center" sx={{ pointerEvents: 'auto' }}>
                      <InfoIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                )}
                {isOptional && (
                  <Typography variant="body1" color="text.secondary">
                    {t('hiring-dashboard-common:common.optional')}
                  </Typography>
                )}
              </Stack>
            }
            secondary={settingSubtitle}
            sx={{
              wordBreak: 'break-word',
              wordWrap: 'normal',
              whiteSpace: 'normal',
              width: '50%',
            }}
          />
          <Stack
            data-testid="SettingsRow-ValueContainer"
            direction="row"
            ml={1}
            width="50%"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack data-testid="SettingsRow-ValueContent" alignItems="flex-start" minWidth={0} flex={1}>
              {typeof settingValue === 'string' && <ExpandableTextView content={settingValue} />}
              {typeof settingValue !== 'string' && settingValue}
              {settingSubvalue && (
                <Typography
                  component="div"
                  sx={{ wordBreak: 'normal', wordWrap: 'normal', whiteSpace: 'normal' }}
                  color="text.secondary"
                >
                  {settingSubvalue}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexShrink={0} ml={1}>
              {isLocked && (
                <Box minWidth="20px">
                  <LockIcon />
                </Box>
              )}
              {onClick && (
                <Box minWidth="20px">
                  <CaretRightIcon />
                </Box>
              )}
            </Stack>
          </Stack>
        </ListItemButton>
      </ListItem>
    </>
  );
};
